import React from 'react';
import { ChildProps } from '@apollo/client/react/hoc';
import { gql } from '@reverbdotcom/commons/src/gql';
import I18n from 'i18n-js';
import CMSListingsCollection from '@reverbdotcom/commons/src/cms/components/cms_listings_collection';
import { Paths } from '@reverbdotcom/commons/src/url_helpers';
import {
  LoggedOutMParticleProfileQuery,
  reverb_search_ListingsSearchRequest_Sort,
} from '@reverbdotcom/commons/src/gql/graphql';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { withMpid, MpidProps } from '@reverbdotcom/commons/src/with_mpid';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { EXCLUDED_SHOP_IDS } from '@reverbdotcom/commons/src/components/watched_listings_fallback';

type Props = ChildProps<MpidProps, LoggedOutMParticleProfileQuery.Query>;

export function MostWatchedListings(props: Props) {
  const { data } = props;
  const user = useUser();
  const topProductTypes = data?.me?.profile?.topMostRecentProductTypes || [];

  useViewTracking({
    eventName: MParticleEventName.ComponentView,
    componentName: 'MostWatchedListings',
    affinityProductTypeSlug: topProductTypes[0],
  }, !props.data.loading);

  let title = I18n.t('discovery.homepage.mostWatchedMusicGear');
  if (topProductTypes.length) {
    const categoryName = I18n.t(`discovery.categories.${topProductTypes[0]}`);
    title = I18n.t('discovery.homepage.mostWatched', { categoryName: categoryName });
  }

  return (
    <CMSListingsCollection
      ctaTargetHref={Paths.marketplace.expand({ sort: 'recent_watchers_count|desc', product_type: topProductTypes[0] })}
      productType={topProductTypes[0]}
      sort={reverb_search_ListingsSearchRequest_Sort.RECENT_WATCHERS_COUNT_DESC}
      title={title}
      trackingName="LoggedOutHomepageMostWatched"
      userShippingRegion={user.shippingRegionCode}
      excludeShopIds={EXCLUDED_SHOP_IDS} // excluding these shop ids removes reverb shipping boxes from results
      maxCount={9}
      mosaicPattern={{
        desktop: '9-tile-center-focus',
        mobile: '5-tile-center-focus',
      }}
      colCount={{ desktop: 6, mobile: 2 }}
      mosaicListingRow
      displayMobileMosaic
    />
  );
}

export const LOGGED_OUT_MPARTICLE_PROFILE_QUERY = gql(`
  query LoggedOutMParticleProfileQuery(
    $mpid: String
  ) {
    me {
      _id
      uuid
      profile(input: {
        mpid: $mpid
      }) {
        topMostRecentProductTypes
      }
    }
  }
`);

const withMpidQuery = withGraphql<Props, LoggedOutMParticleProfileQuery.Query>(
  LOGGED_OUT_MPARTICLE_PROFILE_QUERY,
  {
    options: (props) => {
      return {
        ssr: false,
        variables: {
          mpid: props.mpid,
        },
      };
    },
    skip: (props) => props.mpid === undefined,
  },
);


export const MostWatchedListingsCollection = withMpid<Props>(withMpidQuery(MostWatchedListings));
